import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPersonaChipClass } from '../../../../utils/styles.js';
import { useAuth } from '../../../hooks/useAuth'; 

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// MODALES EXISTENTES EN EL PROYECTO
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';
import RefacturarPoliza from '../../../ui/RefacturarPoliza.jsx';
import ModalDetalleDeuda from '../../../ui/ModalDetalleDeuda.jsx';
import ModalFichaCliente from '../../../ui/ModalFichaCliente.jsx';

// FILTROS Y CONTROLADOR
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import { useRefacturacionPolizasTable } from './useRefacturacionPolizasTable';

// --- FILA MEMOIZADA ---
const TableRow = memo(({ p, isSelected, isLocked, isLockedByMe, columnVisibility, onSelect, onAction, onUnlock, onShowDebt }) => {
    const formattedDate = useMemo(() => {
        if (!p.fechaRefacturacion) return 'N/A';
        return p.fechaRefacturacion.split('-').reverse().join('/');
    }, [p.fechaRefacturacion]);

    return (
        <tr 
            onClick={() => !isLocked && onSelect(p.id)}
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100
                ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''} 
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.vencimientoDate && (
                <td className="p-2.5 px-3 text-sm whitespace-nowrap border-b border-r border-slate-100 text-center text-orange-700 font-bold">
                    <div className="flex flex-col items-center justify-center">
                        <span>{formattedDate}</span>
                        {p.esUltimaRefac && (
                            <span className="text-[8px] font-black text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded mt-0.5 uppercase tracking-tighter">
                                Último cobro
                            </span>
                        )}
                    </div>
                </td>
            )}

            {columnVisibility.nPoliza && (
                <td className="p-2.5 px-3 text-sm text-slate-900 font-bold text-center border-b border-r border-slate-100 tabular-nums">
                    {p.nPoliza}
                </td>
            )}

            {columnVisibility.compania && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <div className="flex items-center justify-center">
                        <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border-b border border-blue-100 rounded-lg w-fit group-hover:bg-white transition-colors shadow-sm">
                            <div className="text-blue-400"><Icons.Company /></div>
                            <span className="text-[11px] text-blue-800 font-bold uppercase truncate max-w-[160px]">{p.compania}</span>
                        </div>
                    </div>
                </td>
            )}

            {columnVisibility.displayNombre && (
                <td className="p-2.5 px-4 text-sm text-slate-900 font-bold border-b border-r border-slate-100" title={p.displayNombre}>
                    <div className="flex items-center justify-between gap-4 text-left">
                        <div className="flex flex-col truncate text-left w-full">
                            <span className="truncate text-left block">{p.displayNombre}</span>
                            {p.esUltimaRefac && (
                                <span className="mt-1 text-[9px] font-black text-amber-700 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded w-fit uppercase tracking-wider animate-pulse flex items-center gap-1 leading-none select-none">
                                    <svg className="w-3 h-3 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                    </svg>
                                    Última cuota • Gestionar en Renovaciones
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {p.tieneDeuda && (
                                <div onClick={(e) => { e.stopPropagation(); onShowDebt(p); }} className="flex items-center text-red-600 animate-pulse cursor-pointer transition-transform p-0.5 rounded-full hover:bg-red-50" title="Ver Deuda">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                                </div>
                            )}
                        </div>
                    </div>
                </td>
            )}

            {columnVisibility.displayDoc && (
                <td className="p-2.5 px-3 text-sm text-slate-600 font-mono text-center border-b border-r border-slate-100">{p.displayDoc}</td>
            )}

            {columnVisibility.riesgo && (
                <td className="p-2.5 px-4 text-sm text-slate-700 whitespace-nowrap border-b border-r border-slate-100 text-left">
                    <div className="flex flex-col truncate text-left">
                        <span className="font-bold text-slate-800 tracking-tight text-left">{p.riesgo}</span>
                        <span className="text-[11px] text-slate-500 font-medium uppercase truncate mt-0.5 text-left">{p.subriesgo || 'General'}</span>
                    </div>
                </td>
            )}

            {columnVisibility.formaPago && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <div className="flex items-center justify-center px-3 h-[26px] bg-blue-50 border border-blue-100 rounded-lg w-fit mx-auto">
                        <span className="text-[11px] text-blue-800 font-bold uppercase">{p.formaPago || '-'}</span>
                    </div>
                </td>
            )}

            {columnVisibility.canal && (
                <td className="p-2.5 px-3 text-sm text-slate-700 whitespace-nowrap border-b border-r border-slate-100 text-left">
                    <div className="flex flex-col truncate text-left">
                        <span className="font-bold text-slate-800 tracking-tight text-left">{p.canal}</span>
                        <span className="text-[11px] text-slate-500 font-medium uppercase truncate mt-0.5 text-left">{p.canalNombre || p.subcanal || '-'}</span>
                    </div>
                </td>
            )}

            {/* Acciones */}
            <td className="p-2.5 px-4 text-right sticky right-0 transition-colors z-20 border-b border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)] bg-white group-hover:bg-slate-50">
                <div className="flex justify-end items-center gap-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'ficha'); }} className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all active:scale-90" title="Ficha Técnica Cliente"><Icons.User /></button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'view'); }} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all active:scale-90" title="Ver Detalles"><Icons.Policy /></button>
                    {isLocked ? (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 animate-pulse shadow-sm">
                            <Icons.Lock /> <span className="text-[9px] font-black uppercase tracking-tighter">{p.bloqueo?.userName || 'Ocupado'}</span>
                        </div>
                    ) : (
                        <>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'refac'); }} className="p-1.5 text-orange-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all active:scale-90" title="Refacturar Póliza"><Icons.ChevronRight /></button>
                            {isLockedByMe && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); onUnlock(p.path); }} className="p-1.5 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar Caso">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const RefacturacionPolizas = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Consumimos nuestro cerebro de negocio estructurado
    const {
        loading,
        needsHydration,
        clientsCache,
        activePoliza,
        showDetailModal,
        showRefacModal,
        showDebtModal,
        showFichaModal,
        showLastRefacAlert,
        lastRefacPolizaInfo,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDetailModal,
        setShowRefacModal,
        setShowDebtModal,
        setShowFichaModal,
        setShowLastRefacAlert,
        setLastRefacPolizaInfo,
        setActivePoliza,
        handleAction,
        handleShowDebt,
        handleRefacSuccess,
        unlockPoliza
    } = useRefacturacionPolizasTable();

    // Desestructuramos el estado de la tabla
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        checkIsLocked: checkIsItemLocked
    } = tableState;

    const columnLabels = {
        vencimientoDate: "Vencimiento (Refact.)", nPoliza: "N° Póliza", compania: "Compañía",
        displayNombre: "Cliente", displayDoc: "DNI/CUIT", riesgo: "Riesgo/Subr", formaPago: "Pago", canal: "Canal"
    };

    // Liberación automática al desmontar pestaña
    useEffect(() => {
        const handleUnload = () => { if (activeItemPathRef.current) unlockPoliza(activeItemPathRef.current); };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [unlockPoliza, activeItemPathRef]);

    // Configuración de Filtros Dinámicos
    const filterConfig = useMemo(() => {
        const getUnique = (list, key) => [...new Set(list.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        
        const selCanal = filterValues.canal || [];
        const dataForCanal = selCanal.length > 0 ? itemsTable.filter(d => selCanal.includes(d.canal)) : itemsTable;
        const canalNombreOptions = [...new Set(dataForCanal.map(d => d.canalNombre))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        const selCanalNombre = filterValues.canalNombre || [];
        const dataForSubcanal = selCanalNombre.length > 0 ? dataForCanal.filter(d => selCanalNombre.includes(d.canalNombre)) : dataForCanal;
        const subcanalOptions = [...new Set(dataForSubcanal.map(d => d.subcanalNombre))].filter(Boolean).sort().map(v => ({label: v, value: v}));

        return [
            { name: 'search', label: 'Buscador', type: 'text', placeholder: 'Cliente o Póliza...', className: 'w-72' },
            { name: 'riesgo', label: 'Riesgo', type: 'select', isMulti: true, options: getUnique(itemsTable, 'riesgo') },
            { name: 'subriesgo', label: 'Subriesgo', type: 'select', isMulti: true, options: getUnique(itemsTable, 'subriesgo') },
            { name: 'compania', label: 'Compañía', type: 'select', isMulti: true, options: getUnique(itemsTable, 'compania') },
            { name: 'formaPago', label: 'Forma Pago', type: 'select', isMulti: true, options: getUnique(itemsTable, 'formaPago') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique(itemsTable, 'canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: canalNombreOptions },
            { name: 'subcanalNombre', label: 'Subcanal / Asociado', type: 'select', isMulti: true, options: subcanalOptions },
            { name: 'vencimientoDate', label: 'Vencimiento', type: 'date-range', className: 'w-64' },
        ];
        
    }, [itemsTable, filterValues]);

    // ◄ PREVENCIÓN DE PARPADEO (needsHydration)
    if (loading || needsHydration) return (
        <div className="flex flex-col items-center justify-center h-screen bg-white space-y-6">
            <div className="w-16 h-16 border-4 border-orange-600/20 border-t-orange-600 rounded-full animate-spin"></div>
            <div className="text-center">
                <span className="font-black text-[10px] uppercase tracking-[0.3em] text-orange-600 animate-pulse">Sincronizando Tablero Refacturaciones</span>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-screen w-full bg-white text-slate-900 overflow-hidden antialiased font-['Inter',_sans-serif] text-left">
            <div className="h-1 bg-orange-600 w-full shrink-0"></div>
            
            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6 text-left">
                    <button type="button" onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><Icons.ArrowLeft /></button>
                    <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">Tablero Refacturaciones</h2>
                </div>
                <div className="flex items-center gap-4 text-left">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm">
                        <div className="bg-orange-600 text-white px-4 py-1.5 rounded-lg text-lg font-black tabular-nums">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Casos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS CON KEY ESTABLE */}
            <FiltrosDinamicos 
                key="refacturacion-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL RESPONSIVE */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white text-left">
                <TableContainer minWidth="2000px">
                    <thead>
                        <tr className="bg-[#01032d] text-white">
                            <SortableHeader label="Vencimiento" sortKey="vencimientoDate" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="N° Póliza" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-center" />
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[320px] text-left" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Riesgo / Subriesgo" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[220px] text-left" />
                            <SortableHeader label="Forma de Pago" sortKey="formaPago" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Canal" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <th className="w-32 p-4 sticky right-0 top-0 z-[65] bg-slate-900 border-b border-slate-700 uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-left">
                        {paginatedData.map(item => {
                            const isLocked = checkIsItemLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <TableRow 
                                    key={item.id} 
                                    p={item} 
                                    isSelected={selectedRowId === item.id} 
                                    isLocked={isLocked}
                                    isLockedByMe={isLockedByMe}
                                    columnVisibility={columnVisibility} 
                                    onSelect={setSelectedRowId} 
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza} 
                                    onShowDebt={handleShowDebt} 
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN COMÚN (Acento naranja de Refacturaciones) */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="resultados"
                    accentClass="text-orange-700 bg-orange-50 border-orange-100"
                />
            </main>

            {/* MODALES RECONECTADOS */}
            <ModalDetalleDeuda show={showDebtModal} onClose={() => setShowDebtModal(false)} poliza={activePoliza} client={activePoliza ? { id: activePoliza.clienteId, ...(clientsCache[activePoliza.clienteId] || {}) } : null} />
            <ModalDetallePoliza show={showDetailModal} onClose={() => { setShowDetailModal(false); setActivePoliza(null); }} poliza={activePoliza} />
            <ModalFichaCliente show={showFichaModal} onClose={() => { setShowFichaModal(false); setActivePoliza(null); }} client={activePoliza ? { id: activePoliza.clienteId, ...(clientsCache[activePoliza.clienteId] || {}) } : null} />
            
            {activePoliza && (
                <RefacturarPoliza 
                    show={showRefacModal} 
                    onClose={() => { unlockPoliza(activePoliza.path); setShowRefacModal(false); setActivePoliza(null); }} 
                    poliza={activePoliza} 
                    freshClientData={clientsCache[activePoliza.clienteId] || { id: activePoliza.clienteId }} 
                    onSuccess={handleRefacSuccess} 
                />
            )}

            {/* MODAL DE ADVERTENCIA PARA GESTIONAR RENOVACIÓN (Exclusivo de Refacturaciones) */}
            {showLastRefacAlert && lastRefacPolizaInfo && (
                <div className="fixed inset-0 bg-slate-900/60 z-[1200] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in text-left">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-amber-100 relative text-left">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-amber-100 text-amber-600 p-2.5 rounded-xl shrink-0">
                                <svg className="w-6 h-6 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                </svg>
                            </div>
                            <div>
                                <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">¡Última Refacturación Registrada!</h4>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Límite de facturación alcanzado</p>
                            </div>
                        </div>

                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2 mb-6 text-left text-xs text-slate-700">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Resumen del Contrato:</p>
                            <div className="grid grid-cols-3 gap-y-1 text-[11px]">
                                <span className="text-slate-400 font-medium col-span-1">Asegurado:</span>
                                <span className="font-bold text-slate-800 col-span-2 truncate">{lastRefacPolizaInfo.displayNombre}</span>
                                
                                <span className="text-slate-400 font-medium col-span-1">N° Póliza:</span>
                                <span className="font-bold text-slate-800 col-span-2">{lastRefacPolizaInfo.nPoliza}</span>

                                <span className="text-slate-400 font-medium col-span-1">Compañía:</span>
                                <span className="font-bold text-slate-800 col-span-2">{lastRefacPolizaInfo.compania}</span>

                                <span className="text-slate-400 font-medium col-span-1">Vto. Final:</span>
                                <span className="font-bold text-orange-700 col-span-2">{lastRefacPolizaInfo.fechaVencimiento?.split('-').reverse().join('/') || 'S/D'}</span>
                            </div>
                        </div>

                        <p className="text-xs text-slate-600 leading-relaxed mb-6">
                            Esta póliza ha cubierto todas sus cuotas y ha desaparecido del Tablero de Refacturaciones. <strong>Ya debe ser gestionada de manera prioritaria en el Tablero de Renovaciones</strong> para renovar su vigencia anual o semestral.
                        </p>

                        <div className="flex gap-3">
                            <button 
                                onClick={() => { setShowLastRefacAlert(false); setLastRefacPolizaInfo(null); setActivePoliza(null); }}
                                className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                            >
                                Ignorar
                            </button>
                            <button 
                                onClick={() => { 
                                    setShowLastRefacAlert(false); 
                                    setLastRefacPolizaInfo(null); 
                                    setActivePoliza(null); 
                                    navigate('/cpanel'); // Redirección
                                }}
                                className="flex-1 py-3 px-4 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-slate-200"
                            >
                                Gestionar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RefacturacionPolizas;